import { message } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { type FieldValues, useFormContext, useWatch } from "react-hook-form";
import { SelectOption } from "../../../../../../../types/select";
import { SectionLoader } from "../../../../../../atoms/SectionLoader/SectionLoader";
import { DecimalUtils } from "../../../../../../libs/utils";
import { useBookingSelector } from "../../../../hooks/useBookingSelector";
import { useTorRules } from "../../../../hooks/useTorRules";
import { type BillingSchedule, type ServiceCharge, useGenerateScheduleBreakdown } from "../api";
import { BillingScheduleBreakdown } from "../billing-schedule";
import { ServiceChargeSection } from "../service-charge";
import { type ServiceChargeTypeValue } from "../service-charge/ServiceChargeFields";
import { useBuildPlanFromPreviousYear } from "../useBuildPlanFromPreviousYear";
import { PaymentPlanFields } from "./PaymentPlanFields";
import { useEscalateRentAfterCopy } from "./useEscalateRentAfterCopy";
import { usePaymentPlanBreakdownStaleness } from "./usePaymentPlanBreakdownStaleness";
import { YearCardHeader } from "./YearCardHeader";

type RentTypeValue = "areaSqft" | "fixed";

export type ServiceChargePlan = {
  installments?: SelectOption<number>;
  rentType?: SelectOption<ServiceChargeTypeValue>;
  chargeValue?: number;
  annualRent?: number;
};

export type PlanFieldItem = {
  id: string;
  installments?: SelectOption<number>;
  rentType?: SelectOption<RentTypeValue>;
  annualRent?: number;
  rentValue?: number;
  unitAreaInSqft?: number;
  securityDeposit?: number;
  noOfYear?: number;
  torPeriod?: SelectOption<number>;
  oldInstallments?: number;
  oldRentType?: string;
  oldAnnualRent?: number;
  oldRentValue?: number;
  oldUnitAreaInSqft?: number;
  oldSecurityDeposit?: number;
  billingScheduleRows?: BillingSchedule[];
  serviceChargeSchedule?: ServiceCharge[];
  serviceCharge?: ServiceChargePlan;
  escalateRentPercent?: number;
};

type YearCardProps = {
  planIndex: number;
  item: PlanFieldItem;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  lastEndDate?: string;
  onBreakdownGenerated?: (lastEndDate: string) => void;
  onPaymentPlanBreakdownStaleChange?: (planId: string, stale: boolean) => void;
  onServiceChargeBreakdownStaleChange?: (planId: string, stale: boolean) => void;
  resetKey?: number;
};

export const YearCard = (props: YearCardProps) => {
  const {
    planIndex,
    item,
    isExpanded,
    onToggle,
    onRemove,
    lastEndDate,
    onBreakdownGenerated,
    onPaymentPlanBreakdownStaleChange,
    onServiceChargeBreakdownStaleChange,
    resetKey = 0,
  } = props;

  const planStableId = item.id;

  const { control, trigger, setValue, getValues, clearErrors } = useFormContext();

  const plan = useWatch({ control, name: `plans[${planIndex}]` });
  const yearNumber = planIndex + 1;

  const installmentsCount: number | undefined = plan?.installments?.label;

  const { leaseId, propertyDetails } = useBookingSelector();
  const { isTurnoverRentOnly, hasValidTorType } = useTorRules();

  const prevResetKeyRef = useRef(resetKey);

  const [breakdownRows, setBreakdownRows] = useState<BillingSchedule[] | null>(
    (item.billingScheduleRows as BillingSchedule[] | undefined) ?? null,
  );

  const [serviceChargeSectionKey, setServiceChargeSectionKey] = useState(0);

  const isBreakdownGenerated = breakdownRows?.length > 0;

  const { generateScheduleBreakdown, isPendingGenerateScheduleBreakdown } = useGenerateScheduleBreakdown();
  const { buildPlanFromPreviousYear } = useBuildPlanFromPreviousYear();
  const escalateRentController = useEscalateRentAfterCopy({ planIndex });

  const { onBreakdownSuccess, onBreakdownCleared } = usePaymentPlanBreakdownStaleness({
    plan: plan as FieldValues,
    yearNumber,
    hasValidTorType,
    isTurnoverRentOnly,
    planStableId,
    breakdownRows,
    onStaleChange: onPaymentPlanBreakdownStaleChange,
  });

  useEffect(() => {
    if (resetKey === 0) {
      prevResetKeyRef.current = 0;
      return;
    }
    if (prevResetKeyRef.current === resetKey) return;
    prevResetKeyRef.current = resetKey;

    setBreakdownRows(null);
    setServiceChargeSectionKey((key) => key + 1);
    setValue(`plans[${planIndex}].billingScheduleRows`, undefined, { shouldDirty: false, shouldValidate: false });
    setValue(`plans[${planIndex}].serviceCharge`, undefined, { shouldDirty: false, shouldValidate: false });
    setValue(`plans[${planIndex}].serviceChargeSchedule`, [], { shouldDirty: false, shouldValidate: false });
    onBreakdownCleared();
  }, [resetKey, planIndex, setValue, onBreakdownCleared]);

  const handleRowsChange = useCallback(
    (rows: BillingSchedule[]) => {
      setValue(`plans[${planIndex}].billingScheduleRows`, rows, { shouldDirty: false, shouldValidate: false });
    },
    [planIndex, setValue],
  );

  const handleGenerateBreakdown = async () => {
    if (!leaseId) {
      message.error("Unable to find lease ID");
      return;
    }

    const planFieldBase = `plans.${planIndex}`;
    const currentPlan = getValues(planFieldBase) as FieldValues;
    const rentType = currentPlan?.rentType?.value ?? plan?.rentType?.value;
    const isAreaSqft = rentType === "areaSqft";
    const isFirstYear = yearNumber === 1;

    if (isTurnoverRentOnly) {
      clearErrors(`${planFieldBase}.installments`);
    }

    if (isAreaSqft) {
      const unitArea = Number(currentPlan?.unitAreaInSqft ?? plan?.unitAreaInSqft ?? propertyDetails?.areaSqft ?? 0);
      const rentValue = currentPlan?.rentValue ?? plan?.rentValue;
      if (unitArea > 0) {
        setValue(`${planFieldBase}.unitAreaInSqft`, unitArea, { shouldValidate: false });
      }
      if (rentValue != null && unitArea > 0) {
        setValue(`${planFieldBase}.annualRent`, DecimalUtils.toNumber(DecimalUtils.multiply(rentValue, unitArea)), {
          shouldValidate: false,
        });
      }
    }

    const validationFields = [
      ...(isTurnoverRentOnly ? [] : [`${planFieldBase}.installments`]),
      `${planFieldBase}.rentType`,
      ...(isAreaSqft
        ? [`${planFieldBase}.rentValue`, `${planFieldBase}.unitAreaInSqft`]
        : [`${planFieldBase}.annualRent`]),
      ...(hasValidTorType ? [`${planFieldBase}.torPeriod`] : []),
      ...(isFirstYear ? [`${planFieldBase}.securityDeposit`] : []),
    ];

    const isValid = await trigger(validationFields as `plans.${number}.installments`[], { shouldFocus: true });
    if (!isValid) return;

    if (!isExpanded) onToggle();

    const hasLastEndDate = Boolean(yearNumber > 1 && lastEndDate);
    const resolvedPlan = getValues(planFieldBase) as FieldValues;

    try {
      const data = await generateScheduleBreakdown({
        leaseId,
        generate: "billingSchedule",
        commercialPaymentPlan: [
          {
            noOfYear: yearNumber,
            installments: resolvedPlan?.installments?.value,
            torPeriod: resolvedPlan?.torPeriod?.value,
            annualRent: resolvedPlan?.annualRent,
            rentType: resolvedPlan?.rentType?.value,
            ...(isAreaSqft
              ? {
                  rentValue: resolvedPlan?.rentValue,
                  unitAreaInSqft: String(resolvedPlan?.unitAreaInSqft ?? propertyDetails?.areaSqft ?? ""),
                }
              : {}),
            ...(isFirstYear ? { securityDeposit: resolvedPlan?.securityDeposit } : {}),
            ...(hasLastEndDate ? { lastEndDate } : {}),
          },
        ],
      });

      if (!data?.length) {
        message.warning("No billing schedule rows were returned. Please check the payment plan values.");
        return;
      }

      setBreakdownRows(data);
      setValue(`plans[${planIndex}].billingScheduleRows`, data, { shouldDirty: false, shouldValidate: false });
      onBreakdownSuccess(getValues(planFieldBase) as FieldValues);
      const endDate = data.at(-1)?.endDate;
      if (endDate) onBreakdownGenerated?.(endDate);
    } catch (error) {
      message.error(
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Failed to generate billing schedule breakdown",
      );
    }
  };

  const hasValue = (value: unknown): boolean => {
    if (value == null) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  };

  const isPreviousYearComplete = useCallback((): boolean => {
    if (planIndex < 1) return false;

    const previousPlan = getValues(`plans[${planIndex - 1}]`) as FieldValues | undefined;
    if (previousPlan == null || typeof previousPlan !== "object") return false;

    const previousRentType = previousPlan?.rentType?.value;
    const isPreviousPerSqFt = previousRentType === "areaSqft";

    const hasInstallments = hasValue(previousPlan?.installments?.value);
    const hasRentType = hasValue(previousRentType);
    const hasAnnualRent = hasValue(previousPlan?.annualRent);
    const hasTorPeriod = !hasValidTorType || hasValue(previousPlan?.torPeriod?.value);
    const hasSecurityDeposit = planIndex > 1 || hasValue(previousPlan?.securityDeposit);
    const hasPerSqftFields =
      !isPreviousPerSqFt || (hasValue(previousPlan?.rentValue) && hasValue(previousPlan?.unitAreaInSqft));

    return hasInstallments && hasRentType && hasAnnualRent && hasTorPeriod && hasSecurityDeposit && hasPerSqftFields;
  }, [getValues, hasValidTorType, planIndex]);

  const handleCopyFromPreviousYear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (planIndex < 1) return;
      if (!isPreviousYearComplete()) return;

      const previousPlan = getValues(`plans[${planIndex - 1}]`);
      const payload = buildPlanFromPreviousYear({ previousPlan, yearNumber });
      if (payload == null) return;

      const currentPlan = getValues(`plans[${planIndex}]`) as FieldValues | undefined;
      if (currentPlan == null || typeof currentPlan !== "object") return;

      const nextPlan = { ...(currentPlan as Record<string, unknown>), ...payload } as FieldValues;
      setValue(`plans[${planIndex}]`, nextPlan, { shouldDirty: true, shouldValidate: false });

      setValue(`plans[${planIndex}].installments`, (payload.installments as FieldValues) ?? null, {
        shouldDirty: true,
        shouldValidate: false,
      });
      setValue(`plans[${planIndex}].rentType`, (payload.rentType as FieldValues) ?? null, {
        shouldDirty: true,
        shouldValidate: false,
      });
      setValue(`plans[${planIndex}].torPeriod`, (payload.torPeriod as FieldValues) ?? null, {
        shouldDirty: true,
        shouldValidate: false,
      });
      escalateRentController.handleCopyFromPreviousYearEscalation();

      setBreakdownRows(null);
      onBreakdownCleared();
      setServiceChargeSectionKey((k) => k + 1);
    },
    [
      buildPlanFromPreviousYear,
      escalateRentController,
      getValues,
      isPreviousYearComplete,
      onBreakdownCleared,
      planIndex,
      setValue,
      yearNumber,
    ],
  );

  const canCopyFromPreviousYear = isPreviousYearComplete();

  return (
    <div
      style={{
        border: "1px solid var(--gray-300)",
        borderRadius: 8,
        marginBottom: 12,
        background: "#fff",
      }}
    >
      <div
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <YearCardHeader
          yearNumber={yearNumber}
          isBreakdownGenerated={isBreakdownGenerated}
          installmentsCount={installmentsCount}
          annualRent={plan?.annualRent ?? 0}
          breakdownStartDate={breakdownRows?.at(0)?.startDate}
          planIndex={planIndex}
          isExpanded={isExpanded}
          onCopyFromPreviousYear={handleCopyFromPreviousYear}
          canCopyFromPreviousYear={canCopyFromPreviousYear}
          onRemove={onRemove}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateRows: isExpanded ? "1fr" : "0fr",
          transition: "grid-template-rows 0.35s ease",
        }}
      >
        <div style={{ overflow: "hidden", opacity: isExpanded ? 1 : 0, transition: "opacity 0.25s ease" }}>
          <div style={{ padding: "16px", borderTop: "1px solid var(--gray-200)" }}>
            <PaymentPlanFields
              planIndex={planIndex}
              item={item}
              hasValidTorType={hasValidTorType}
              isPendingGenerateScheduleBreakdown={isPendingGenerateScheduleBreakdown}
              escalateRentController={escalateRentController}
              onGenerateBreakdown={handleGenerateBreakdown}
            />

            <SectionLoader isLoading={isPendingGenerateScheduleBreakdown} containerStyle={{ minHeight: "10rem" }}>
              {isBreakdownGenerated && breakdownRows && (
                <BillingScheduleBreakdown
                  rows={breakdownRows}
                  maxInstallments={plan?.installments?.value ?? 1}
                  maxTorPeriod={plan?.torPeriod?.value ?? 1}
                  yearNumber={yearNumber}
                  hasValidTorType={hasValidTorType}
                  annualRent={plan?.annualRent ? Number(plan.annualRent) : undefined}
                  onRowsChange={handleRowsChange}
                />
              )}
            </SectionLoader>

            {isBreakdownGenerated && (
              <ServiceChargeSection
                key={`${planIndex}-${serviceChargeSectionKey}`}
                planIndex={planIndex}
                yearNumber={yearNumber}
                lastEndDate={lastEndDate}
                planStableId={planStableId}
                onServiceChargeBreakdownStaleChange={onServiceChargeBreakdownStaleChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
